/**
 * KAN-77: shared "is this question answered?" predicate.
 *
 * This is the single source of truth for answered-detection so the pre-submit
 * question-status grid and the submit gate (`allQuestionsAnswered()`) never
 * disagree. A question is considered answered as soon as the learner has any
 * recorded selection, which for every question type means the slot in
 * `userAnswers` is no longer the initial null sentinel:
 *   - multiple-choice / true-false: an index (incl. 0) or a boolean (incl. false)
 *   - select-all: an array (possibly empty after deselect)
 *   - multiple-choice-grid / checkbox-grid: a row array (rows may be -1 blank
 *     "no-answer" sentinels per KAN-274, which still counts as an answer)
 *
 * Points are irrelevant: a 0-point question is answered/unanswered purely on
 * selection, identical to any other answerable question.
 *
 * KAN-526: TEXT answers are the exception, because for a text field the slot
 * being non-null does not mean the learner wrote anything. There are four
 * distinct empty states and they must all read the same way:
 *
 *   undefined     slot never written
 *   null          rendered but untouched
 *   ''            typed into, then cleared
 *   '   \n'       whitespace only
 *
 * The first two were already unanswered; the last two were reported as
 * ANSWERED, which put a checkmark against a visibly blank field. A string is
 * an answer only once it has non-whitespace content.
 *
 * NOTE: this predicate answers "did the learner answer this?" and nothing
 * else. Whether an unanswered question may still be SUBMITTED is a separate
 * question with a separate answer - see `isAnsweredForSubmit` in QuizPanel,
 * which exempts optional information-collection fields. Folding those two
 * together is what caused KAN-526.
 */
export function isQuestionAnswered(answer: unknown): boolean {
  if (answer === null || answer === undefined) {
    return false;
  }
  if (typeof answer === 'string') {
    return answer.trim().length > 0;
  }
  return true;
}
